import type { ChartOptions, TooltipModel } from 'chart.js';
import { Chart as ChartJS } from 'chart.js';
import { css } from '@emotion/react';
import { color, spacing, Typography } from '@uniquegood/realworld-studio-design';
import { Line } from 'react-chartjs-2';
import { useRef, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { RowFlex } from '@/styles';

interface TrendChartProps {
  labels: string[];
  playData: number[];
  completeData: number[];
  totalPlayUsers: number;
  totalCompleteUsers: number;
  isSingleDay?: boolean; // 하루인 경우 시간 형식으로 표시
  isMonthly?: boolean; // 두 달 이상인 경우 월 단위로 표시
}

export default function TrendChart({
  labels,
  playData,
  completeData,
  totalPlayUsers,
  totalCompleteUsers,
  isSingleDay = false,
  isMonthly = false
}: TrendChartProps) {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // 그라데이션 생성
  const createGradient = (ctx: CanvasRenderingContext2D, color1: string, color2: string) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);
    return gradient;
  };

  useEffect(() => {
    const chart = chartRef.current;
    if (chart) {
      const { ctx } = chart;
      const playGradient = createGradient(ctx, 'rgba(26, 26, 26, 0.1)', 'rgba(26, 26, 26, 0.02)');
      const completeGradient = createGradient(
        ctx,
        'rgba(120, 0, 255, 0.15)',
        'rgba(120, 0, 255, 0.02)'
      );

      chart.data.datasets[0].backgroundColor = playGradient;
      chart.data.datasets[1].backgroundColor = completeGradient;
      chart.update();
    }
  }, [playData, completeData]);

  // 커스텀 툴팁 핸들러
  const externalTooltipHandler = useCallback(
    (context: { chart: ChartJS; tooltip: TooltipModel<'line'> }) => {
      const { chart, tooltip } = context;
      const tooltipEl = tooltipRef.current;

      if (!tooltipEl) return;

      // 툴팁 숨기기
      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = '0';
        return;
      }

      // 툴팁 내용 설정
      if (tooltip.body) {
        const dataPoint = tooltip.dataPoints[0];
        const { dataIndex } = dataPoint;
        const { label: datasetLabel } = dataPoint.dataset;
        const currentValue = dataPoint.parsed.y;

        // 타이틀 생성
        let titleText = '';
        const originalDate = labels[dataIndex];
        const date = dayjs(originalDate);

        if (isSingleDay) {
          const datePart = date.format('YYYY-MM-DD');
          const hours = date.hour();
          let timePart = '';
          if (hours === 0) timePart = '12am';
          else if (hours === 12) timePart = '12pm';
          else if (hours < 12) timePart = `${hours}am`;
          else timePart = `${hours - 12}pm`;
          titleText = `[${datasetLabel}] ${datePart}, ${timePart}`;
        } else if (isMonthly) {
          const yearMonth = date.format('YYYY-MM');
          titleText = `[${datasetLabel}] ${yearMonth}`;
        } else {
          const datePart = date.format('YYYY-MM-DD');
          titleText = `[${datasetLabel}] ${datePart}`;
        }

        // 비교 텍스트 생성
        let comparisonHtml = '';
        if (dataIndex > 0) {
          const previousValue = dataPoint.dataset.data[dataIndex - 1] as number;
          const diff = currentValue - previousValue;
          const diffStr = diff >= 0 ? `+${diff}` : `${diff}`;

          let comparisonText = '';
          if (isSingleDay) {
            comparisonText = '1시간 전 대비';
          } else if (isMonthly) {
            comparisonText = '이전달 대비';
          } else {
            comparisonText = '전날 대비';
          }
          comparisonHtml = `<span class="tooltip-comparison">${comparisonText} ${diffStr}명</span>`;
        }

        tooltipEl.innerHTML = `
          <div class="tooltip-title">${titleText}</div>
          <div class="tooltip-body">
            <span class="tooltip-value">${currentValue}명</span>
            ${comparisonHtml}
          </div>
        `;
      }

      // 위치 계산
      const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
      tooltipEl.style.opacity = '1';
      tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
      tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
    },
    [labels, isSingleDay, isMonthly]
  );

  // 시간 포맷팅 헬퍼 함수
  const formatTimeLabel = (dateString: string): string => {
    const date = dayjs(dateString);
    const hours = date.hour();

    // 12시간 형식으로 변환 (12am, 1am, ..., 11am, 12pm, 1pm, ..., 11pm)
    if (hours === 0) return '12am';
    if (hours === 12) return '12pm';
    if (hours < 12) return `${hours}am`;
    return `${hours - 12}pm`;
  };

  // 차트 데이터 메모이제이션 (labels, playData, completeData, isSingleDay, isMonthly 변경 시에만 재계산)
  const chartData = useMemo(
    () => ({
      labels: labels.map((time) => {
        if (isSingleDay) {
          // 하루인 경우 시간 형식으로 표시 (예: 12am, 3am, 6am, 9am, 12pm, 3pm, 6pm, 9pm)
          return formatTimeLabel(time);
        }
        if (isMonthly) {
          // 두 달 이상인 경우 월 단위로 표시 (예: 7월, 8월)
          const date = dayjs(time);
          return `${date.month() + 1}월`;
        }
        // 여러 날인 경우 날짜 형식으로 표시 (예: 12월 12일)
        const date = dayjs(time);
        return `${date.month() + 1}월 ${date.date()}일`;
      }),
      datasets: [
        {
          label: '플레이 인원',
          data: playData,
          fill: true,
          borderColor: color.text_default,
          backgroundColor: 'rgba(26, 26, 26, 0.05)',
          borderWidth: 2,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: color.text_default,
          pointHoverBorderColor: color.surface_default_default,
          pointHoverBorderWidth: 2
        },
        {
          label: '완료 인원',
          data: completeData,
          fill: true,
          borderColor: color.palette_primary_purple_100,
          backgroundColor: 'rgba(120, 0, 255, 0.08)',
          borderWidth: 2,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: color.palette_primary_purple_100,
          pointHoverBorderColor: color.surface_default_default,
          pointHoverBorderWidth: 2
        }
      ]
    }),
    [labels, playData, completeData, isSingleDay, isMonthly]
  );

  /**
   * 0~10: 2 단위 (y축 0, 2, 4, 6, 8, 10)
   * 10~50: 10 단위 (y축 0, 10, 20, 30, 40, 50)
   * 50~100: 20 단위 (y축 0, 20, 40, 60, 80, 100)
   * 100~200: 50 단위 (y축 0, 50, 100, 150, 200)
   * 200~500: 100 단위 (y축 0, 100, 200, 300, 400, 500)
   * 500~1,000: 200 단위 (y축 0, 200, 400, 600, 800, 1000)
   * 1,000~5,000: 1,000 단위 (y축 0, 1000, 2000, 3000, 4000, 5000)
   * 5,000~10,000: 2,000 단위(y축 0, 2000, 4000, 6000, 8000, 10000)
   * 10,000~50,000: 10,000 단위 (y축 0, 10000, 20000, 30000, 40000, 50000)
   * 50,000 이상: 20,000 단위 (y축 0, 20000, 40000, 60000, ..최대값이 75000이라면 80000까지 표시)
   */
  // y축 동적 계산
  const yAxisConfig = useMemo(() => {
    const allData = [...playData, ...completeData];
    const maxValue = Math.max(...allData, 0);

    // 적절한 stepSize와 max 계산
    let stepSize: number;
    let max: number;

    if (maxValue <= 10) {
      stepSize = 2;
      max = 10;
    } else if (maxValue <= 50) {
      stepSize = 10;
      max = 50;
    } else if (maxValue <= 100) {
      stepSize = 20;
      max = 100;
    } else if (maxValue <= 200) {
      stepSize = 50;
      max = 200;
    } else if (maxValue <= 500) {
      stepSize = 100;
      max = 500;
    } else if (maxValue <= 1000) {
      stepSize = 200;
      max = 1000;
    } else if (maxValue <= 5000) {
      stepSize = 1000;
      max = 5000;
    } else if (maxValue <= 10000) {
      stepSize = 2000;
      max = 10000;
    } else if (maxValue <= 50000) {
      stepSize = 10000;
      max = 50000;
    } else {
      stepSize = 20000;
      // 50,000 이상은 20,000 단위로 올림 (예: 75,000 -> 80,000)
      max = Math.ceil(maxValue / stepSize) * stepSize;
      if (max === maxValue) {
        max += stepSize; // 최대값에 딱 걸리면 한 칸 더 표시
      }
    }

    return { max, stepSize };
  }, [playData, completeData]);

  const chartOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'nearest',
        intersect: false
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 12,
              family: 'Pretendard'
            },
            color: color.text_subdued,
            autoSkip: false,
            callback: (_, index) => {
              const label = chartData.labels?.[index];
              if (!label) return '';

              // 하루(시간별): 3시간 간격으로 레이블 표시 (12am, 3am, 6am, 9am, 12pm, 3pm, 6pm, 9pm)
              if (isSingleDay) {
                // 3시간 간격으로만 레이블 표시 (인덱스 0, 3, 6, 9, 12, 15, 18, 21)
                if (index % 3 === 0) {
                  return label;
                }
                return '';
              }

              // 두 달 이상(월별): 모든 레이블 표시
              if (isMonthly) {
                return label;
              }

              // 이틀 이상 ~ 두 달 미만: 일자 표시
              const totalDays = labels.length;

              // 14일 미만: 1일씩 표시
              if (totalDays < 14) {
                return label;
              }

              // 14일 이상: 3일씩 표시 (인덱스가 3의 배수일 때만)
              if (index % 3 === 0) {
                return label;
              }

              return '';
            }
          }
        },
        y: {
          beginAtZero: true,
          min: 0,
          max: yAxisConfig.max,
          grid: {
            color: color.border_default_subdued,
            drawBorder: false
          },
          ticks: {
            stepSize: yAxisConfig.stepSize,
            font: {
              size: 12,
              family: 'Pretendard'
            },
            color: color.text_subdued,
            padding: 0
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false,
          external: externalTooltipHandler
        }
      }
    }),
    [externalTooltipHandler, labels, isSingleDay, isMonthly, chartData.labels, yAxisConfig]
  );

  return (
    <div css={WrapperStyle}>
      {/* 요약 카드 */}
      <div css={SummaryCardsStyle}>
        <div css={SummaryCardStyle}>
          <Typography type="body" cssStyle={LabelStyle}>
            기간 내 플레이 인원
          </Typography>
          <Typography type="heading" cssStyle={ValueStyle}>
            {totalPlayUsers}명
          </Typography>
        </div>
        <div css={SummaryCardStyle}>
          <Typography type="body" cssStyle={LabelStyle}>
            기간 내 완료 인원
          </Typography>
          <Typography type="heading" cssStyle={ValueStyle}>
            {totalCompleteUsers}명
          </Typography>
        </div>
      </div>

      {/* 범례 */}
      <div css={LegendStyle}>
        <div css={LegendItemStyle}>
          <div css={LegendDotStyle(color.text_default)} />
          <Typography type="body">플레이 인원</Typography>
        </div>
        <div css={LegendItemStyle}>
          <div css={LegendDotStyle(color.palette_primary_purple_100)} />
          <Typography type="body">완료 인원</Typography>
        </div>
      </div>

      {/* 차트 */}
      <div css={ChartContainerStyle}>
        <Line ref={chartRef} data={chartData} options={chartOptions} />
        <div ref={tooltipRef} css={CustomTooltipStyle} />
      </div>
    </div>
  );
}

const WrapperStyle = css`
  width: 100%;
`;

const SummaryCardsStyle = css`
  display: flex;
  gap: ${spacing.margin.medium};
  margin-bottom: ${spacing.margin.xlarge2};
`;

const SummaryCardStyle = css`
  width: 160px;
  height: 88px;
  padding: 20px 24px;
  background-color: #f5f5f5;
  border-radius: ${spacing.borderRadius.medium};
`;

const LabelStyle = css`
  color: ${color.text_default};
  margin-bottom: 8px;
`;

const ValueStyle = css`
  color: #282f89;
  font-weight: 700;
`;

const LegendStyle = css`
  ${RowFlex}
  justify-content: flex-end;
  gap: ${spacing.margin.large};
  margin-bottom: ${spacing.margin.medium};
`;

const LegendItemStyle = css`
  ${RowFlex}
  align-items: center;
  gap: 8px;
`;

const LegendDotStyle = (dotColor: string) => css`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${dotColor};
`;

const ChartContainerStyle = css`
  width: 100%;
  height: 400px;
  position: relative;
`;

const CustomTooltipStyle = css`
  position: absolute;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.12);
  padding: 16px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.1s ease;
  transform: translate(-50%, -100%);
  margin-top: -12px;
  white-space: nowrap;
  z-index: 10;

  .tooltip-title {
    font-family: 'Pretendard', sans-serif;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    color: #1a1a1a;
    margin-bottom: 8px;
  }

  .tooltip-body {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .tooltip-value {
    font-family: 'Pretendard', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #1a1a1a;
  }

  .tooltip-comparison {
    font-family: 'Pretendard', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: #7800ff;
  }
`;
